// src/features/interview/hooks/useInterviewFlow.js
import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getConfigurations,
  uploadAnswer,
  checkInterviewState,
  resumeInterviewReq,
  deleteInterviewFiles,
  proceedToNextQuestion,
  uploadCandidateSnapshot as uploadSnapshotService,
  generateUploadUrls,
  uploadToS3,
  submitAnswerWithS3Keys
} from "../services/interviewService";
import useAudioRecorder from "./useAudioRecorder";
import { VIOLATION_THRESHOLDS, COOLDOWN_PERIOD, GIF_VARIANTS, TIMEOUTS, AUDIO_CONSTANTS, CONFETTI_COLORS } from "../constants";
import { useDispatch, useSelector } from "react-redux";
import { resetCandidateResponse } from "../../../../store/slices/miscSlice";
import { enqueueMicLog } from "../../../../telemetry/logQueue";

/**
 * useInterviewFlow
 * - encapsulates interview state, media handling, timers, proctoring alerts, and network flows
 *
 * Call: const { state, actions, refs } = useInterviewFlow(interviewId, navigate);
 */
export const useInterviewFlow = (interview_id, navigate, options = {}) => {
  const isLiveAvatar = options?.isLiveAvatar ?? false;
  // Environment buckets (consistent with original)
  const BUCKET_NAME = process.env.REACT_APP_BUCKET_NAME;
  const AWS_REGION_NAME = process.env.REACT_APP_AWS_REGION_NAME;

  const dispatch = useDispatch();
  // Refs
  const audioRef = useRef(null); // will be attached to <audio ref={audioRef}/>
  const proctoringVideoRef = useRef(null);
  const audioStreamRef = useRef(null);
  const timerRef = useRef(null);
  const isTransitioningQuestionRef = useRef(false);
  const showProctoringAlertRef = useRef(false);
  const pendingAudioAfterModalRef = useRef(null);
  const technicalProbeAudioRef = useRef(null);
  const technicalTimeoutRef = useRef(null);
  const lastMultipleFaceViolationTime = useRef(null);
  const lastProhibitedObjectViolationTime = useRef(null);

  // State - copied from original
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [isNextButtonEnabled, setIsNextButtonEnabled] = useState(false);
  const [isQuestionVisible, setIsQuestionVisible] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isFinalizingUploads, setIsFinalizingUploads] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [showStartButton, setShowStartButton] = useState(false);
  const [showResumeButton, setShowResumeButton] = useState(false);
  const [isInterviewComplete, setIsInterviewComplete] = useState(false);
  const [interviewScoreThreshold, setInterviewScoreThreshold] = useState(60);
  const [interviewNotFound, setInterviewNotFound] = useState(false);
  const [errormsg, setErrormsg] = useState("");
  const [isNextQuestionLoading, setIsNextQuestionLoading] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [questionTimerSeconds, setQuestionTimerSeconds] = useState(TIMEOUTS.QUESTION_TIMER_SECONDS);
  const [timeLeft, setTimeLeft] = useState(TIMEOUTS.QUESTION_TIMER_SECONDS);
  const [timerActive, setTimerActive] = useState(false);
  const [candidateName, setCandidateName] = useState("Candidate");
  const [currentGif, setCurrentGif] = useState("stable");
  const [videoIncluded, setVideoIncluded] = useState(false);
  const [useVoiceAnimation, setUseVoiceAnimation] = useState(false);
  const [storingEnabled, setStoringEnabled] = useState(false);
  const [isPlayingThankYou, setIsPlayingThankYou] = useState(false);
  const [secondaryCameraEnabled, setSecondaryCameraEnabled] = useState(false);
  const [hasResumed, setHasResumed] = useState(false);
  const [isInterviewActive, setIsInterviewActive] = useState(false);

  // Proctoring / alert state
  const pendingFaceAlertRef = useRef(false);
  const [multipleFaceViolationCount, setMultipleFaceViolationCount] = useState(0);
  const [prohibitedObjectViolationCount, setProhibitedObjectViolationCount] = useState(0);
  const pendingMultipleFaceAlertRef = useRef(false);
  const pendingProhibitedObjectAlertRef = useRef(false);
  const isWaitingForTechnicalAcknowledgeRef = useRef(false);
  const [bodyPostureViolationCount, setBodyPostureViolationCount] = useState(0);
  const pendingBodyPostureAlertRef = useRef(false);
  const lastBodyPostureViolationTime = useRef(null);

  const [gazeViolationCount, setGazeViolationCount] = useState(0);
  const pendingGazeAlertRef = useRef(false);
  const lastGazeViolationTime = useRef(null);

  const [stickyAlertCount, setStickyAlertCount] = useState(0);

  // Mic check resume state
  const [showResumeForMicCheck, setShowResumeForMicCheck] = useState(false);
  const [preservedInterviewState, setPreservedInterviewState] = useState(null);

  // Technical issue handling
  const [technicalProbeMessage, setTechnicalProbeMessage] = useState("");
  const [technicalButtonText, setTechnicalButtonText] = useState("Acknowledge");
  const [hideNextButton, setHideNextButton] = useState(false);
  const [isPostAcknowledgment, setIsPostAcknowledgment] = useState(false);
  const [technicalIssueCount, setTechnicalIssueCount] = useState(0);

  const [showProctoringAlert, setShowProctoringAlert] = useState(false);
  const [proctoringAlertMessage, setProctoringAlertMessage] = useState("");
  const [showSecondaryCameraGate, setShowSecondaryCameraGate] = useState(false);

  const [cumulativeBodyPostureViolations, setCumulativeBodyPostureViolations] = useState(0);
  const [cumulativeGazeViolations, setCumulativeGazeViolations] = useState(0);

  const [isIntentionalFullscreenChange, setIsIntentionalFullscreenChange] = useState(false);
  const [tabSwitchScreenshotEnabled, setTabSwitchScreenshotEnabled] = useState(false);

  const candidateResponse = useSelector((state) => state.miscSlice.candidateResponse);
  const candidateResponseRef = useRef(candidateResponse);
  candidateResponseRef.current = candidateResponse;
  // Alerts log
  const [alertLog, setAlertLog] = useState({
    tab_switch: { count: 0, lastAlertTime: null, glowing: false },
    multi_monitor: { count: 0, lastAlertTime: null, glowing: false },
    fullscreen_exit: { count: 0, lastAlertTime: null, glowing: false },
  });
  const [isAlertPanelOpen, setIsAlertPanelOpen] = useState(false);
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);

  // Per-answer mic health telemetry (logs only, never blocks the answer).
  const currentQuestionIndexRef = useRef(-1);
  const onAnswerHealth = useCallback(
    (report) => {
      if (report.ended || report.zero_windows > 0 || report.mute_events > 0) {
        console.warn("MIC HEALTH - answer report:", report);
      }
      enqueueMicLog({
        interview_id,
        record_type: "answer_health",
        question_index: currentQuestionIndexRef.current,
        ...report,
      });
    },
    [interview_id]
  );

  // Mid-answer mic death → re-ask (D1), never resume/splice.
  // Refs, not closure values: watchHealth attaches before setIsRecording(true)
  // commits, so a closure over isRecording would always read false.
  const liveMicDeathTrippedRef = useRef(false);
  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;
  const currentQuestionRef = useRef(currentQuestion);
  currentQuestionRef.current = currentQuestion;
  const timeLeftRef = useRef(timeLeft);
  timeLeftRef.current = timeLeft;
  const onLiveMicDeath = useCallback((info) => {
    if (liveMicDeathTrippedRef.current) return;
    if (!isRecordingRef.current) {
      console.info(
        `MIC RECOVERY - trip signal (${info.reason}) ignored: not recording ` +
        `(health watch outlived the answer, or trip fired after submit)`
      );
      return;
    }
    liveMicDeathTrippedRef.current = true;
    console.warn(
      `MIC RECOVERY - ENTERING recovery screen | question_index=${currentQuestionIndexRef.current} ` +
      `| reason=${info.reason} | device="${info.device_label}" ` +
      `| answer discarded (never uploaded), will re-ask same question on a fresh pass`
    );
    setTimerActive(false);
    audioRecorder.stopAll();
    setIsRecording(false);
    setPreservedInterviewState({
      currentQuestionIndex: currentQuestionIndexRef.current,
      currentQuestion: currentQuestionRef.current,
      timeLeft: timeLeftRef.current,
      isRecording: false,
    });
    setShowResumeForMicCheck(true);
  }, []);
  const audioRecorder = useAudioRecorder({
    storingEnabled,
    proctoringVideoRef,
    interviewId: interview_id,
    onAnswerHealth,
    onLiveMicDeath,
  });

  // In-flight background video uploads — kept off the interview turn's
  // critical path; awaited once at interview end (see handleSubmit).
  const pendingVideoUploadsRef = useRef([]);
  // Live count of uploads not yet settled — used to warn before closing the
  // tab while a recording is still being saved.
  const pendingUploadCountRef = useRef(0);

  useEffect(() => {
    currentQuestionIndexRef.current = currentQuestionIndex;
  }, [currentQuestionIndex]);

  // Warn before closing/refreshing while any video uploads are in-flight
  // (both mid-interview background uploads and the end-of-interview drain).
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (pendingUploadCountRef.current > 0 || isFinalizingUploads) {
        e.preventDefault();
        e.returnValue = "Your interview responses are still being saved. If you leave now, some recordings may be lost.";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFinalizingUploads]);
  // Keep proctoring alert ref in sync with state (for use in async closures)
  useEffect(() => {
    showProctoringAlertRef.current = showProctoringAlert;
  }, [showProctoringAlert]);
  // Auto-close alert panel logic (copied)
  useEffect(() => {
    const isFullscreenAlertActive = alertLog.fullscreen_exit.count > 0;
    if (isAlertPanelOpen && !isFullscreenAlertActive) {
      if (alertPanelTimerRef.current) {
        clearTimeout(alertPanelTimerRef.current);
      }
      const timer = setTimeout(() => {
        setIsAlertPanelOpen(false);
        alertPanelTimerRef.current = null;
      }, COOLDOWN_PERIOD.ALERT_PANEL_AUTO_CLOSE_MS);

      alertPanelTimerRef.current = timer;
    } else {
      if (alertPanelTimerRef.current) {
        clearTimeout(alertPanelTimerRef.current);
        alertPanelTimerRef.current = null;
      }
    }
    return () => {
      if (alertPanelTimerRef.current) {
        clearTimeout(alertPanelTimerRef.current);
      }
    };
  }, [isAlertPanelOpen, alertLog.fullscreen_exit.count]);


  //To Clear candidate response on unmount
  useEffect(() => {
    if(!isInterviewActive){
      dispatch(resetCandidateResponse());
    }
  }, [isInterviewActive]);
  // small ref for alert panel timer (keeps it local)
  const alertPanelTimerRef = useRef(null);

  // Prevent text-selection, copy, paste and show toast (copied)
  useEffect(() => {
    const preventSelection = (e) => {
      // A <select> opens its dropdown as mousedown's DEFAULT action —
      // preventDefault here silently kills it (and focus). Exempt form
      // controls so the resume screen's mic picker stays usable; the
      // anti-copy blocking still applies everywhere else.
      if (e.target && e.target.closest && e.target.closest("select, option, input, textarea")) {
        return;
      }
      e.preventDefault();
      return false;
    };
    document.addEventListener("selectstart", preventSelection);
    document.addEventListener("mousedown", preventSelection);

    const preventCopyPasteCut = (e) => {
      e.preventDefault();
      toast.warn("Copy/paste is disabled during the interview.");
    };
    document.addEventListener("copy", preventCopyPasteCut);
    document.addEventListener("paste", preventCopyPasteCut);
    document.addEventListener("cut", preventCopyPasteCut);

    const preventContextMenu = (e) => {
      // e.preventDefault();
      toast.warn("Right click is disabled during the interview.");
    };
    document.addEventListener("contextmenu", preventContextMenu);

    return () => {
      document.removeEventListener("selectstart", preventSelection);
      document.removeEventListener("mousedown", preventSelection);
      document.removeEventListener("copy", preventCopyPasteCut);
      document.removeEventListener("paste", preventCopyPasteCut);
      document.removeEventListener("cut", preventCopyPasteCut);
      document.removeEventListener("contextmenu", preventContextMenu);
    };
  }, []);

  // Fetch configurations (on mount)
  const fetchConfigurations = useCallback(async () => {
    try {
      const response = await getConfigurations(interview_id);
      setVideoIncluded(response.data.video_included);
      setUseVoiceAnimation(response.data.interview_v1_animation ?? false);
      setInterviewScoreThreshold(response.data.interview_score_threshold);
      setStoringEnabled(response.data.storing_enabled);
      setSecondaryCameraEnabled(response.data.secondary_camera_enabled || false);
      setTabSwitchScreenshotEnabled(response.data.tab_switch_screenshot || false);
      if (response.data.question_timer_seconds) {
        setQuestionTimerSeconds(response.data.question_timer_seconds);
        setTimeLeft(response.data.question_timer_seconds);
      }
    } catch (error) {
      console.error("Error fetching configurations:", error);
      toast.error("Failed to load configurations. Enabling video for proctoring.");
      setVideoIncluded(false);
    }
  });

  useEffect(() => {
    if (isLiveAvatar) return; // LiveAvatar portal fetches config itself inside init() before session starts
    fetchConfigurations();
  }, []);

  // Pre-load greeting audio on mount so it's buffered before user clicks Begin Interview
  const greetingAudioRef = useRef(null);
  useEffect(() => {
    const audio = new Audio(
      `https://${BUCKET_NAME}.s3.${AWS_REGION_NAME}.amazonaws.com/${interview_id}_audio_questions/greeting.mp3`
    );
    audio.preload = "auto";
    audio.load();
    greetingAudioRef.current = audio;
    return () => {
      greetingAudioRef.current = null;
    };
  }, []);

  // gif mapping and switching
  useEffect(() => {
    if (isRecording) {
      setCurrentGif("listening");
    } else if (isPlaying) {
      setCurrentGif("speaking");
    } else {
      setCurrentGif("stable");
    }
  }, [isRecording, isPlaying]);

  const gifVariants = GIF_VARIANTS;

  // ----- Alerts helpers
  const getActiveAlerts = useCallback(() => {
    const alerts = [];
    if (alertLog.fullscreen_exit.count > 0) {
      return [
        {
          type: "fullscreen_exit",
          message: "Please enter fullscreen mode to continue",
          count: alertLog.fullscreen_exit.count,
          lastAlertTime: alertLog.fullscreen_exit.lastAlertTime,
          glowing: alertLog.fullscreen_exit.glowing,
          isClickable: true,
        },
      ];
    }
    if (alertLog.tab_switch.count > 0) {
      alerts.push({
        type: "tab_switch",
        message: "Tab switch detected",
        count: alertLog.tab_switch.count,
        lastAlertTime: alertLog.tab_switch.lastAlertTime,
        glowing: alertLog.tab_switch.glowing,
        isClickable: false,
      });
    }
    if (alertLog.multi_monitor.count > 0) {
      alerts.push({
        type: "multi_monitor",
        message: "Multiple monitors detected",
        count: alertLog.multi_monitor.count,
        lastAlertTime: alertLog.multi_monitor.lastAlertTime,
        glowing: alertLog.multi_monitor.glowing,
        isClickable: false,
      });
    }
    const fullscreenAlerts = alerts.filter((a) => a.type === "fullscreen_exit");
    const otherAlerts = alerts
      .filter((a) => a.type !== "fullscreen_exit")
      .sort((a, b) => (b.lastAlertTime || 0) - (a.lastAlertTime || 0));
    return [...fullscreenAlerts, ...otherAlerts];
  }, [alertLog]);

  const enterFullscreen = () => {
    setIsIntentionalFullscreenChange(true);
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }

    setTimeout(() => {
      setIsIntentionalFullscreenChange(false);
    }, 500);
  };

  const handleAlertClick = (alertType) => {
    if (alertType === "fullscreen_exit") {
      enterFullscreen();
    }
  };

  const navigateToNextAlert = () => {
    const activeAlerts = getActiveAlerts();
    setCurrentAlertIndex((prev) => (prev + 1) % activeAlerts.length);
  };

  const navigateToPrevAlert = () => {
    const activeAlerts = getActiveAlerts();
    setCurrentAlertIndex(
      (prev) => (prev - 1 + activeAlerts.length) % activeAlerts.length
    );
  };

  const fullscreenActive = alertLog.fullscreen_exit.count > 0;

  const handleAlertIconClick = () => {
    setIsAlertPanelOpen((open) => !open);
    if (!isAlertPanelOpen) {
      setStickyAlertCount(0);
      setCurrentAlertIndex(0);
      setAlertLog((prev) => ({
        ...prev,
        tab_switch: { ...prev.tab_switch, glowing: false },
        multi_monitor: { ...prev.multi_monitor, glowing: false },
        fullscreen_exit: { ...prev.fullscreen_exit, glowing: false },
      }));
    }
  };

  // ----- Proctoring violation handlers
  const handleFaceViolation = () => {
    pendingFaceAlertRef.current = true;
  };

  const handleGazeViolation = () => {
    const now = Date.now();
    const lastViolationTime = lastGazeViolationTime.current;
    
    if (!lastViolationTime || now - lastViolationTime >= COOLDOWN_PERIOD.GAZE_COOLDOWN_MS) {
      lastGazeViolationTime.current = now;
      setCumulativeGazeViolations((prev) => prev + 1);
      setGazeViolationCount((prev) => {
        const newCount = prev + 1;
        if (newCount >= VIOLATION_THRESHOLDS.GAZE) {
          pendingGazeAlertRef.current = true;
          // //console.log(`Gaze violation tier 1 reached (${cumulativeGazeViolations + 1} total) - will add audio alert`);
          return 0;
        }
        return newCount;
      });
    }
  };

  const handleMultipleFaceViolation = () => {
    const now = Date.now();
    const lastViolationTime = lastMultipleFaceViolationTime.current;
    if (!lastViolationTime || now - lastViolationTime >= COOLDOWN_PERIOD.MULTIPLE_FACE_COOLDOWN_MS) {
      lastMultipleFaceViolationTime.current = now;
      setMultipleFaceViolationCount((prev) => {
        const newCount = prev + 1;
        if (newCount >= VIOLATION_THRESHOLDS.MULTIPLE_FACE) {
          pendingMultipleFaceAlertRef.current = true;
          return 0;
        }
        return newCount;
      });
    } else {
      // cooldown active — ignore
    }
  };

  const handleProhibitedObjectViolation = () => {
    const now = Date.now();
    const lastViolationTime = lastProhibitedObjectViolationTime.current;
    if (!lastViolationTime || now - lastViolationTime >= COOLDOWN_PERIOD.PROHIBITED_OBJECT_COOLDOWN_MS) {
      lastProhibitedObjectViolationTime.current = now;
      setProhibitedObjectViolationCount((prev) => {
        const newCount = prev + 1;
        if (newCount >= VIOLATION_THRESHOLDS.PROHIBITED_OBJECT) {
          pendingProhibitedObjectAlertRef.current = true;
          return 0;
        }
        return newCount;
      });
    } else {
      // cooldown active - ignore
    }
  };

  const handleBodyPostureViolation = () => {
    const now = Date.now();
    const lastViolationTime = lastBodyPostureViolationTime.current;
    
    if (!lastViolationTime || now - lastViolationTime >= COOLDOWN_PERIOD.BODY_POSTURE_COOLDOWN_MS) {
      lastBodyPostureViolationTime.current = now;
      setCumulativeBodyPostureViolations((prev) => prev + 1);
      setBodyPostureViolationCount((prev) => {
        const newCount = prev + 1;
        if (newCount >= VIOLATION_THRESHOLDS.BODY_POSTURE) {
          pendingBodyPostureAlertRef.current = true;
          //console.log(`Body posture violation tier 1 reached (${cumulativeBodyPostureViolations + 1} total) - will add audio alert`);
          return 0;
        }
        return newCount;
      });
    }
  };

  const handleStickyAlert = (alertType) => {
    setStickyAlertCount((prev) => prev + 1);
    if (alertType === "fullscreen_exit") {
      setIsAlertPanelOpen(true);
    }
    setAlertLog((prev) => {
      const now = Date.now();
      const prevAlert = prev[alertType];
      const newCount = (prevAlert?.count || 0) + 1;
      const shouldGlow =
        !prevAlert?.lastAlertTime ||
        now - prevAlert.lastAlertTime >= COOLDOWN_PERIOD.ALERT_WINDOW_MS;
      return {
        ...prev,
        [alertType]: {
          count: newCount,
          lastAlertTime: now,
          glowing: shouldGlow,
        },
      };
    });
  };

  const handleViolation = (violationType, meta) => {
    // //console.log(`Proctoring violation: ${violationType}`, meta);
  };

  const handleCameraReady = (isReady) => {
    // //console.log(`Camera ready: ${isReady}`);
  };

  const activateProctoring = () => {
    setIsInterviewActive(true);
  };

  // Stop any currently playing question audio and clean up its state
  const stopCurrentAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.onended = null; 
    }
  };

  const handleFullscreenExitViolation = () => {
    if (isIntentionalFullscreenChange) {
      //console.log("Intentional fullscreen change - ignoring violation");
      return;
    }

    if (currentQuestionIndex === -1 && !isInterviewActive) {
      //console.log("Fullscreen exited before interview start - ignoring");
      return;
    }

    setProctoringAlertMessage(
      "We detected you exited fullscreen mode. Please remain in fullscreen throughout the interview to ensure proper monitoring."
    );
    setShowProctoringAlert(true);
    setIsRecording(false);
    setIsNextQuestionLoading(false);
    stopCurrentAudio(); 
    audioRecorder.stopAudioStream();
  };

  const handleTabSwitchViolation = () => {
    setIsIntentionalFullscreenChange(true);
    setProctoringAlertMessage(
      "We detected tab switching activity. Please stay on the interview tab throughout the session."
    );
    setShowProctoringAlert(true);
    setIsRecording(false);
    setIsNextQuestionLoading(false);
    stopCurrentAudio();
    audioRecorder.stopAudioStream();
    setTimeout(() => {
      setIsIntentionalFullscreenChange(false);
    }, 500 );
  };


  const handleScreenShareStopedViolation = () => {
     setIsIntentionalFullscreenChange(true);
    // setProctoringAlertMessage(
    //   "We detected that screen sharing has stopped. Please ensure screen sharing is active throughout the interview."
    // );
    // setShowProctoringAlert(true);
    setIsRecording(false);
    setIsNextQuestionLoading(false);
    audioRecorder.stopAudioStream();
    // enterFullscreen();
    setTimeout(() => {
      setIsIntentionalFullscreenChange(false);
    }, 500 );
  }

  // ----- Interview flow functions (play greeting/question/thankyou)
  const playGreetingAudio = async () => {
    try {
      setIsInterviewActive(true);
      const audioGreeting = greetingAudioRef.current ?? new Audio(
        `https://${BUCKET_NAME}.s3.${AWS_REGION_NAME}.amazonaws.com/${interview_id}_audio_questions/greeting.mp3`
      );
      audioGreeting.currentTime = 0;
      audioGreeting.onplaying = () => {
        setIsPlaying(true);
        audioGreeting.onplaying = null;
      };
      await audioGreeting.play();
      audioGreeting.onended = async() => {
        setIsPlaying(false);
        const snapshotFile = await takeCandidateSnapshot();
        if (snapshotFile) {
          await uploadCandidateSnapshot(snapshotFile);
        }
        startRecording();
      };
    } catch (error) {
      console.error("Error playing greeting audio:", error);
      toast.error("Something went wrong");
      setTimeout(() => {
        navigate(`/candidate/${interview_id}/interviewinstructions`);
      }, 2000);
      setIsPlaying(false);
      
    }
  };

  const handleStartInterview = () => {
    enterFullscreen();
    playGreetingAudio();
    setShowStartButton(false);
  };

const handleTechnicalResumeComplete = (micCheckPassed) => {
  console.warn(
    `MIC RECOVERY - EXITING recovery screen | micCheckPassed=${micCheckPassed} ` +
    `| resuming question_index=${preservedInterviewState?.currentQuestionIndex} ` +
    `with a fresh recording (never resumes/splices the discarded one)`
  );
  if (preservedInterviewState) {
    // Check if interview hasn't started yet (still at initial screen)
    if (preservedInterviewState.currentQuestionIndex === -1) {
      // Return to initial state - show start button, hide everything else
      setShowResumeForMicCheck(false);
      setPreservedInterviewState(null);
      setShowStartButton(true);
      setIsQuestionVisible(false);
      setIsRecording(false);
    } else {
      // Interview was in progress - restore to recording state
      setIsInterviewActive(true);
      
      //Add delay to ensure proctoring camera reinitializes
      setTimeout(() => {
        setCurrentQuestionIndex(preservedInterviewState.currentQuestionIndex);
        setCurrentQuestion(preservedInterviewState.currentQuestion);
        setTimeLeft(preservedInterviewState.timeLeft);
        setShowResumeForMicCheck(false);
        setPreservedInterviewState(null);
        setShowStartButton(false);
        setIsQuestionVisible(true);
        
        //Wait for camera to be ready before starting recording
        const checkCameraAndStart = () => {
          const hasVideoStream = proctoringVideoRef.current?.srcObject;
          const videoTracks = hasVideoStream?.getVideoTracks();
          const isVideoReady = videoTracks && videoTracks.length > 0 && videoTracks[0].readyState === 'live';
          
          //console.log("🔍 Camera check:", {
          //   hasVideoStream: !!hasVideoStream,
          //   videoTracks: videoTracks?.length || 0,
          //   isVideoReady,
          //   proctoringVideoRef: proctoringVideoRef.current,
          //   srcObject: proctoringVideoRef.current?.srcObject,
          // });
          
          if (isVideoReady) {
            //console.log("✅ Camera ready - starting recording");
            startRecording();
          } else {
            //console.log("⏳ Camera not ready, waiting 500ms...");
            setTimeout(checkCameraAndStart, 500);
          }
        };
        
        // Start checking after a brief delay to allow ProctoringSystem to initialize
        setTimeout(checkCameraAndStart, 1000);
      }, 100);
    }
  }
};

  const handleNormalResumeComplete = (micCheckPassed) => {
    setTimeout(() => {
      resumeInterview();
    }, 1000);
  };

  // Technical acknowledge flow
  const handleTechnicalAcknowledge = async () => {
    try {
      //console.log("🔧 User acknowledged technical issue");
      setHideNextButton(true);
      if (technicalTimeoutRef.current) clearTimeout(technicalTimeoutRef.current);
      isWaitingForTechnicalAcknowledgeRef.current = false;
      technicalProbeAudioRef.current = null;
      setTechnicalProbeMessage("");
      setTechnicalButtonText("Acknowledge");
      setIsQuestionVisible(false);
      setIsRecording(false);
      setIsNextQuestionLoading(true);

      const response = await proceedToNextQuestion(interview_id);

      if (response.data.message === "Interview Complete") {
        await playThankYouAudio();
      } else {
        setCurrentQuestion(response.data.question_clean || response.data.question);

        if (response.data.is_replacement) {
          //console.log("🔄 Replacement question received for same index");
        } else {
          setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
        }

        setIsPostAcknowledgment(true);
        playQuestionAudio(
          response.data.currentQuestionIndex,
          response.data.audio_url,
          false
        );
      }
    } catch (error) {
      console.error("Error acknowledging technical issue:", error);
      toast.error("Error processing acknowledgment. Please try again.");
      if (technicalTimeoutRef.current) clearTimeout(technicalTimeoutRef.current);
      isWaitingForTechnicalAcknowledgeRef.current = false;
      setTechnicalProbeMessage("");
      setIsPostAcknowledgment(false);
    } finally {
      setIsNextQuestionLoading(false);
    }
  };

  // Resume interview from server
  const resumeInterview = async () => {
    try {
      enterFullscreen();
      setShowResumeButton(false);
      setHasResumed(true);
      setIsInterviewActive(true);
      if (isLiveAvatar) return; // LiveAvatar manages its own flow — don't fetch questions
      if(currentQuestionIndex < 0){
        return;
      }
      const response = await resumeInterviewReq(interview_id);
      if (response.data.message === "Interview Complete") {
        handleSubmit();
      } else if (response.data.type === "secondary_camera_disconnected") {
        setShowSecondaryCameraGate(true);
      } else {
        setCurrentQuestion(response.data.question_clean || response.data.question);
        setCurrentQuestionIndex(response.data.currentQuestionIndex);
        playQuestionAudio(response.data.currentQuestionIndex, response.data.audio_url);
      }
    } catch (error) {
      console.error("Error resuming interview:", error.message);
    }
  };

  // 1s delay (same as handleNormalResumeComplete) lets the interview UI remount
  // before resumeInterview plays audio.
  const handleSecondaryCameraRecovered = () => {
    setShowSecondaryCameraGate(false);
    setTimeout(() => {
      resumeInterview();
    }, 1000);
  };

  // Check interview state on mount
  useEffect(() => {
    const checkInterviewStateLocal = async () => {
      try {
        const response = await checkInterviewState(interview_id);
        const data = response.data;
        if (data.message) {
          setInterviewNotFound(true);
          setErrormsg(data.message);
        } else {
          setCurrentQuestionIndex(data.currentQuestionIndex);
          setCandidateName(data.candidate_name);

          if (data.technical_state && data.technical_state.is_in_technical_mode) {
            isWaitingForTechnicalAcknowledgeRef.current = true;
            setTechnicalProbeMessage(data.technical_state.message || "");
            setTechnicalButtonText(data.technical_state.button_text || "Acknowledge");
          }

          if (data.interview_status === "Complete") {
            setIsInterviewComplete(true);
          } else {
            if (data.currentQuestionIndex === -1) {
              setShowStartButton(true);
            } else {
              if (!hasResumed) {
                setShowResumeButton(true);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error checking interview state:", error);
        // No candidate token (or an expired one) for this browser/tab -- send back
        // to OTP verification instead of leaving the interview shell showing with
        // no real data behind it (every subsequent action would 401 anyway).
        if (error.response?.status === 401) {
          navigate(`/candidate/${interview_id}/scheduletest`, { replace: true });
        }
      }
    };

    if (!hasResumed) checkInterviewStateLocal();
  }, [interview_id, hasResumed, navigate]);

  // cleanup technical timeout on unmount
  useEffect(() => {
    return () => {
      if (technicalTimeoutRef.current) {
        clearTimeout(technicalTimeoutRef.current);
      }
    };
  }, []);

  // Thank you audio
  const playThankYouAudio = async () => {
    try {
      setIsRecording(false);
      setIsNextQuestionLoading(false);
      setIsPlaying(true);
      setIsPlayingThankYou(true);
      const thankYouAudio = new Audio(
        `https://${BUCKET_NAME}.s3.${AWS_REGION_NAME}.amazonaws.com/${interview_id}_audio_questions/thankyou.mp3`
      );
      await thankYouAudio.play();
      thankYouAudio.onended = async () => {
        setIsPlaying(false);
        setIsPlayingThankYou(false);
        await handleSubmit();
      };
    } catch (error) {
      console.error("Error playing thank you audio:", error);
      setIsPlaying(false);
      setIsPlayingThankYou(false);
      await handleSubmit();
    }
  };

  // Start recording (audio + possibly video)
  const startRecording = async () => {
    //console.log("Started Start Recording")
    liveMicDeathTrippedRef.current = false; // fresh answer, guardian re-armed
    const result = await audioRecorder.start();
    
    if (!result.success) {
      const error = result.error;
      let errorMessage = "";
      
      if (error.name === "NotAllowedError") {
        errorMessage = "Permission denied. Please allow microphone access and try again.";
      } else if (error.name === "NotFoundError") {
        errorMessage = "No microphone found. Please connect one and refresh the page.";
      } else if (error.name === "NotReadableError") {
        errorMessage = "Selected microphone can't be accessed. Try selecting a different one.";
      } else {
        errorMessage = "Couldn't access microphone. Please check permissions.";
      }
      toast.error(errorMessage);

      setPreservedInterviewState({
        currentQuestionIndex,
        currentQuestion,
        timeLeft,
        isRecording: false,
      });

      setShowResumeForMicCheck(true);
      return;
    }

    // Success - update UI state
    setIsRecording(true);
    setTimeLeft(questionTimerSeconds);
    setTimerActive(true);
    setTimeout(() => setIsNextButtonEnabled(true), 5000);
  };

  // Upload answer and handle server response
  const getquestion_interactivity = async (audioBlob, videoBlob = null) => {
    try {
      // Generate pre-signed URLs
      const urlResponse = await generateUploadUrls(
        interview_id,
        currentQuestionIndex,
        storingEnabled && videoBlob !== null
      );
      
      const { audio_url, audio_key, video_url, video_key, final_video_url, answer_index } = urlResponse.data;
      
      // Step 2: Upload audio directly to S3
      await new Promise(resolve => setTimeout(resolve, 0));
      await uploadToS3(audio_url, audioBlob, 'audio/mpeg');
      
      // Step 3: Upload video in the BACKGROUND (not awaited) so it stays off
      // the turn's critical path. Retry once; all uploads are drained at
      // interview end. The pending count drives the "saving…" finalizing UI.
      let uploadedVideoKey = null;
      if (storingEnabled && videoBlob && video_url) {
        pendingUploadCountRef.current += 1;
        const upload = uploadToS3(video_url, videoBlob, 'video/mp4')
          .catch(() => uploadToS3(video_url, videoBlob, 'video/mp4'))
          .finally(() => {
            pendingUploadCountRef.current -= 1;
          });
        pendingVideoUploadsRef.current.push(upload);
        uploadedVideoKey = video_key;
      }
      
      // Step 4: Prepare alert flags
      const shouldAddFaceAlert = pendingFaceAlertRef.current;
      const shouldAddMultipleFaceAlert = pendingMultipleFaceAlertRef.current;
      const shouldAddProhibitedObjectAlert = pendingProhibitedObjectAlertRef.current;
      const shouldAddBodyPostureAlert = pendingBodyPostureAlertRef.current;
      const shouldAddGazeAlert = false;

      const shouldShowBodyPostureModal = cumulativeBodyPostureViolations >= VIOLATION_THRESHOLDS.BODY_POSTURE_MODAL;
      const shouldShowGazeModal = false;

      // Reset alert flags
      
      if (shouldAddFaceAlert) {
        pendingFaceAlertRef.current = false;
      }
      if (shouldAddMultipleFaceAlert) {
        pendingMultipleFaceAlertRef.current = false;
      }
      if (shouldAddProhibitedObjectAlert) {
        pendingProhibitedObjectAlertRef.current = false;
      }
      if (shouldAddBodyPostureAlert) { 
        pendingBodyPostureAlertRef.current = false;
      }
      if (shouldAddGazeAlert) { 
        pendingGazeAlertRef.current = false;
      }

      // Step 4.5: Force AssemblyAI to finalize any in-flight turn before reading
      // the answer, so a delayed transcript isn't attributed to the next question.
      const flushStartedAt = performance.now();
      await audioRecorder.flushFinalTranscript();
      console.log(`[Flow] flushFinalTranscript blocked for ${(performance.now() - flushStartedAt).toFixed(0)}ms`);

      // Step 5: Submit answer with S3 keys
      //console.log("📤 Submitting answer with S3 keys...");
      console.log("➡️ [video-test] submitting answer / requesting next question"); // TEMP: remove after testing
      const response = await submitAnswerWithS3Keys(
        interview_id,
        audio_key,
        uploadedVideoKey,
        {
          face: shouldAddFaceAlert ? 'true' : 'false',
          multipleFace: shouldAddMultipleFaceAlert ? 'true' : 'false',
          prohibitedObject: shouldAddProhibitedObjectAlert ? 'true' : 'false',
          bodyPosture: shouldAddBodyPostureAlert ? 'true' : 'false',
          gaze: 'false',
          bodyPostureModal: shouldShowBodyPostureModal ? 'true' : 'false',
          gazeModal: 'false',
        },
        candidateResponseRef.current
      );
      dispatch(resetCandidateResponse());

      // Handle response (same as before)
      if (response.data.type === "technical_probe") {
        //console.log("🔧 Technical probe received:", response.data);
        audioRecorder.stopAudioStream();
        
        if (response.data.technical_issue_count) {
          setTechnicalIssueCount(response.data.technical_issue_count);
        }

        setCurrentQuestion(response.data.message);
        setTechnicalProbeMessage(response.data.message);
        setTechnicalButtonText(response.data.button_text || "Acknowledge");
        isWaitingForTechnicalAcknowledgeRef.current = true;
        technicalProbeAudioRef.current = { index: response.data.currentQuestionIndex || currentQuestionIndex, url: response.data.audio_url };

        if (response.data.technical_issue_count > 2) {
          //console.log(`🔧 Technical issue ${response.data.technical_issue_count}/3`);
        }

        technicalTimeoutRef.current = setTimeout(() => {
          //console.log("🕐 Technical acknowledge timeout - auto-proceeding");
          handleTechnicalAcknowledge();
        }, TIMEOUTS.TECHNICAL_TIMEOUT_MS);

        return {
          isProbeResponse: true,
          questionIndex: response.data.currentQuestionIndex || currentQuestionIndex,
          audioUrl: response.data.audio_url,
        };
      }

      if (response.data.type === "technical_threshold_reached") {
        //console.log("🚨 Technical threshold reached - showing mic check resume:", response.data);
        audioRecorder.stopAudioStream();
        setPreservedInterviewState({
          currentQuestionIndex,
          currentQuestion,
          timeLeft,
          isRecording,
        });

        setShowResumeForMicCheck(true);
        return -999;
      }

      if (response.data.type === "proctoring_threshold_reached") {
        if (response.data.violation_type === "body_posture") {
          setCumulativeBodyPostureViolations(0);
        } else if (response.data.violation_type === "gaze") {
          setCumulativeGazeViolations(0);
        }
        //console.log("Proctoring threshold reached → showing modal");
        setProctoringAlertMessage(response.data.message);
        setShowProctoringAlert(true);
        setIsNextQuestionLoading(false);
        return -999; // pause flow
      }

      if (response.data.type === "secondary_camera_disconnected") {
        // Answer is already committed server-side; the gate only withholds the
        // next question until the phone camera is back.
        setShowSecondaryCameraGate(true);
        setIsNextQuestionLoading(false);
        return -999; // pause flow
      }

      if (response.data.message && response.data.message === "Interview Complete") {
        return null;
      } else {
        setCurrentQuestion(response.data.question_clean || response.data.question);
        return response.data;
      }
    } catch (error) {
      console.error("Error in getQuestion:", error.message);
      toast.error("Weak Internet Connection Detected. Please switch to a stable network to continue");

      if (technicalTimeoutRef.current) clearTimeout(technicalTimeoutRef.current);
      isWaitingForTechnicalAcknowledgeRef.current = false;
      setTechnicalProbeMessage("");
      setIsPostAcknowledgment(false);

      return currentQuestionIndex;
    }
  };

  // Stop recording and send the blobs
  const stopRecordingAndProceed = async () => {
    // Re-entrance guard: prevent double-call from timer expiry + manual click
    if (isTransitioningQuestionRef.current) return;

    setShowTimer(false);
    setTimerActive(false);
    if (timerRef.current) clearInterval(timerRef.current);
    setIsNextButtonEnabled(false);
    setIsQuestionVisible(false);
    setIsNextQuestionLoading(true);
    setIsRecording(false);
    isTransitioningQuestionRef.current = true; // mark transition in-flight
    pendingAudioAfterModalRef.current = null; // clear any previous pending data

    try {
      // Get blobs from the audio recorder hook
      const { audioBlob, videoBlob } = await audioRecorder.stopAndGetBlobs();
      // Shadow check: a near-empty blob means the answer was recorded from a
      // dead pipeline — log it (no flow change in this phase).
      if (!audioBlob || audioBlob.size < 1000) {
        enqueueMicLog({
          interview_id,
          record_type: "answer_blob_suspect",
          question_index: currentQuestionIndexRef.current,
          blob_size: audioBlob ? audioBlob.size : 0,
        });
      }
      //console.log("Audio blob size:", audioBlob?.size, "Type:", audioBlob?.type);
      const result = await getquestion_interactivity(audioBlob, storingEnabled ? videoBlob : null);
      if (result === -999) {
        isTransitioningQuestionRef.current = false;
        setIsNextQuestionLoading(false);
        setIsRecording(false);
        return;
      } else if (result !== null) {
        // Build audio params from whichever result shape we got
        let audioParams = null;
        if (typeof result === "object" && result.isProbeResponse) {
          audioParams = { index: result.questionIndex, url: result.audioUrl };
        } else if (typeof result === "object") {
          setCurrentQuestion(result.question_clean || result.question);
          setCurrentQuestionIndex(result.currentQuestionIndex);
          audioParams = { index: result.currentQuestionIndex, url: result.audio_url };
        } else if (typeof result === "number") {
          audioParams = { index: result, url: null };
        }

        if (audioParams) {
          if (showProctoringAlertRef.current) {
            // Modal is open — defer audio to handleProctoringAcknowledge
            pendingAudioAfterModalRef.current = audioParams;
          } else {
            playQuestionAudio(audioParams.index, audioParams.url);
          }
        }

        setIsNextQuestionLoading(false);
      } else {
        setIsNextQuestionLoading(false);
        await playThankYouAudio();
      }
    } catch (error) {
      console.error("Error stopping recording:", error);
      toast.error("Something went wrong while stopping the recording.");
    } finally {
      isTransitioningQuestionRef.current = false;
    }
  };

  const handleProctoringAcknowledge = async () => {
    setShowProctoringAlert(false);
    showProctoringAlertRef.current = false; // sync ref immediately (useEffect would be async)
    setProctoringAlertMessage("");
 
    if (proctoringAlertMessage.includes("fullscreen mode")) {
      enterFullscreen();
    }

    if (isLiveAvatar) return; // LiveAvatar session continues on its own — just clear the alert

    if (currentQuestionIndex === -1 || !isInterviewActive || isInterviewComplete || isPlayingThankYou) {
      return;
    }
 
    // If stopRecordingAndProceed is in-flight, let it finish — it will play audio
    if (isTransitioningQuestionRef.current) {
      setHideNextButton(false);
      return;
    }
 
    // If transition already completed while modal was open, play the deferred audio.
    const pending = pendingAudioAfterModalRef.current;
    if (pending) {
      pendingAudioAfterModalRef.current = null;
      setHideNextButton(false);
      playQuestionAudio(pending.index, pending.url);
      return;
    }
 
    // If waiting for technical acknowledge (mic issue), replay probe audio and return.
    if (isWaitingForTechnicalAcknowledgeRef.current && technicalProbeAudioRef.current) {
      setHideNextButton(false);
      playQuestionAudio(technicalProbeAudioRef.current.index, technicalProbeAudioRef.current.url);
      return;
    }
 
    // No transition was in progress — call resumeInterviewReq to restore current question.
    setIsNextQuestionLoading(true);
    setHideNextButton(false);
 
    try {
      const response = await resumeInterviewReq(interview_id);
      const data = response.data;

      if (data.message === "Interview Complete") {
        await playThankYouAudio();
      } else if (data.type === "secondary_camera_disconnected") {
        setShowSecondaryCameraGate(true);
      } else {
        setCurrentQuestion(data.question);
        setCurrentQuestionIndex(data.currentQuestionIndex || currentQuestionIndex);
        playQuestionAudio(data.currentQuestionIndex, data.audio_url);
      }
    } catch (err) {
      console.error("Failed to proceed after proctoring alert:", err);
      toast.error("Could not continue. Please check your connection.");
    } finally {
      setIsNextQuestionLoading(false);
    }
  };
  // Add after the fetchConfigurations function
const takeCandidateSnapshot = async () => {
  if (!proctoringVideoRef.current) return null;

  const video = proctoringVideoRef.current;
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth || 640;
  canvas.height = video.videoHeight || 480;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], "candidate_snapshot.jpg", { 
          type: "image/jpeg" 
        });
        resolve(file);
      } else {
        resolve(null);
      }
    }, "image/jpeg", 0.95);
  });
};

const uploadCandidateSnapshot = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await uploadSnapshotService(interview_id, formData); 
    //console.log("Candidate snapshot uploaded:", response.data.photo_url);
  } catch (error) {
    console.error("Failed to upload snapshot:", error);
  }
};

  // Play question audio (index or custom URL)
    const playQuestionAudio = (
    index,
    customAudioUrl = null,
    skipRecording = false
  ) => {
    let retryCount = 0;
    const maxRetries = 4;

    const tryPlayAudio = async () => {
      try {
        // Use custom audio URL if provided (for technical probes), otherwise use standard format
        let audio;
      if (customAudioUrl) {
        audio = customAudioUrl;
      } else {
        // For index 0 (first question after intro), use 0.mp3
        // For all others, use the standard {index + 1} format
        const audioIndex = index === 0 ? 0 : index + 1;
        audio = `https://${BUCKET_NAME}.s3.${AWS_REGION_NAME}.amazonaws.com/${interview_id}_audio_questions/${audioIndex}.mp3`;
      }
        
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.onended = null;
        audioRef.current.src = audio;

        await audioRef.current.load();
        await new Promise((resolve) => setTimeout(resolve, 100));
        await audioRef.current.play();
        setIsPlaying(true);

        audioRef.current.onended = () => {
          setIsPlaying(false);
          setIsQuestionVisible(true);
          setHideNextButton(false); // Reset after question audio finishes

          // FIXED: Handle different flow states
          if (isPostAcknowledgment) {
            // Post-acknowledgment: Start recording automatically and reset state
            setIsPostAcknowledgment(false);
            startRecording();
          } else if (!isWaitingForTechnicalAcknowledgeRef.current && !skipRecording) {
            // Normal flow: Start recording
            startRecording();
          }
          // Technical probe: Do nothing, wait for acknowledgment
        };
      } catch (error) {
        console.error("Audio failed to play:", error);
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(tryPlayAudio, 4000);
        } else {
          console.error(`Failed to play audio after ${maxRetries} attempts`);
          // alert(
          //   "Interview Disconnected,Please check your internet connection and try again."
          // );
        }
      }
    };

    tryPlayAudio();
  };

  // Submit / finish interview
  const handleSubmit = async () => {
    setIsSubmitLoading(true);
    setIsInterviewActive(false);
    if (audioRecorder.audioStreamRef.current) {
      audioRecorder.audioStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      audioRecorder.audioStreamRef.current = null;
    }
    audioRecorder.stopAll();

    if (technicalTimeoutRef.current) clearTimeout(technicalTimeoutRef.current);
    isWaitingForTechnicalAcknowledgeRef.current = false;
    setTechnicalProbeMessage("");
    setTechnicalButtonText("Acknowledge");
    setIsPostAcknowledgment(false);

    // Wait for any in-flight background video uploads before finalizing, so
    // we never lose a video. Show a "saving — don't close" screen with live
    // progress. Interview is already over, so this wait costs nothing.
    const uploads = pendingVideoUploadsRef.current;
    if (uploads.length > 0) {
      let done = 0;
      setUploadProgress({ done: 0, total: uploads.length });
      setIsFinalizingUploads(true);
      await Promise.allSettled(
        uploads.map((p) =>
          p.finally(() => {
            done += 1;
            setUploadProgress({ done, total: uploads.length });
          })
        )
      );
      setIsFinalizingUploads(false);
    }

    try {
      const endTime = new Date();
      const payload = {
        delete_video_files: storingEnabled,
        video_included: videoIncluded,
        interview_score_threshold: interviewScoreThreshold,
      };
      deleteInterviewFiles(interview_id, endTime.toISOString(), payload);
      navigate(`/thankyou/${interview_id}`);
    } catch (error) {
      console.error("Error deleting audio files:", error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  // Confetti helper
  const createConfetti = () => {
    const colors = CONFETTI_COLORS;
    return Array.from({ length: 50 }).map((_, i) => (
      {
        key: i,
        style: {
          left: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 2}s`,
          transform: `rotate(${Math.random() * 360}deg)`,
        },
        colorClass: colors[i % colors.length],
      }
    ));
  };

  // Timer handling (counts down)
  useEffect(() => {
    if (timerActive) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 30) {
            setShowTimer(true);
          }
          if (prev <= 1) {
            stopRecordingAndProceed();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerActive]); 

  // Check interview state function for external control (exposed)
  const checkInterviewStateExtern = async () => {
    try {
      const response = await checkInterviewState(interview_id);
      return response.data;
    } catch (e) {
      console.error("Error checking interview state:", e);
      return null;
    }
  };

  // Expose a compact state + actions set to be consumed by components
  const state = {
    // raw UI state
    isPlaying,
    isRecording: audioRecorder.isRecording,
    currentQuestionIndex,
    currentQuestion,
    isNextButtonEnabled,
    isQuestionVisible,
    isSubmitLoading,
    isFinalizingUploads,
    uploadProgress,
    showResumeButton,
    isInterviewComplete,
    interviewScoreThreshold,
    interviewNotFound,
    errormsg,
    isNextQuestionLoading,
    hideNextButton,
    isWaitingForTechnicalAcknowledge: isWaitingForTechnicalAcknowledgeRef.current,
    technicalButtonText,
    showTimer,
    timeLeft,
    timerActive,
    candidateName,
    currentGif,
    gifVariants,
    videoIncluded,
    useVoiceAnimation,
    storingEnabled,
    isPlayingThankYou,
    secondaryCameraEnabled,
    hasResumed,
    isInterviewActive,
    // proctoring
    multipleFaceViolationCount,
    prohibitedObjectViolationCount,
    bodyPostureViolationCount,
    gazeViolationCount,
    stickyAlertCount,
    alertLog,
    isAlertPanelOpen,
    currentAlertIndex,
    showStartButton,
    showResumeForMicCheck,
    showProctoringAlert,
    proctoringAlertMessage,
    showSecondaryCameraGate,
    cumulativeBodyPostureViolations,
    cumulativeGazeViolations,
  };

  const actions = {
    handleStartInterview,
    handleTechnicalResumeComplete,
    handleNormalResumeComplete,
    handleTechnicalAcknowledge,
    playQuestionAudio,
    resumeInterview,
    handleSecondaryCameraRecovered,
    stopRecordingAndProceed,
    startRecording,
    handleSubmit,
    handleAlertIconClick,
    navigateToNextAlert,
    navigateToPrevAlert,
    handleAlertClick,
    handleFaceViolation,
    handleMultipleFaceViolation,
    handleProhibitedObjectViolation,
    handleBodyPostureViolation,
    handleGazeViolation,
    handleStickyAlert,
    handleViolation,
    handleCameraReady,
    fetchConfigurations: fetchConfigurations,
    checkInterviewState: checkInterviewStateExtern,
    setShowResumeForMicCheck,
    setPreservedInterviewState,
    setCurrentQuestion,
    setCurrentQuestionIndex,
    setIsNextButtonEnabled,
    setIsQuestionVisible,
    setIsInterviewComplete,
    handleProctoringAcknowledge,
    handleFullscreenExitViolation,
    handleTabSwitchViolation,
    handleScreenShareStopedViolation,
    setIsIntentionalFullscreenChange,
    activateProctoring,
  };

  const refs = {
    audioRef,
    proctoringVideoRef,
    audioStreamRef: audioRecorder.audioStreamRef,
  };

  return { state, actions, refs, createConfetti, getActiveAlerts };
};

